<div class="pt-container">
  <!-- ── COVER IMAGE ── -->
  <!--
    DOUBLE click, not single.

    Single click now belongs to selection in ProjectsComponent, and
    the two cannot share the gesture: a click that navigates away can
    never be the first half of a Ctrl+Click or Shift+Click range. This
    is where every file manager puts open, so it is also what the
    operator will try first.

    The explicit open buttons further down the tile are still single
    click. They are <button>s, which the grid's click handler skips,
    so a one-click route to a specific Draft or Book survives.
  -->
  <div class="pt-cover" (dblclick)="open(book)">
    <app-project-book-cover-image [book]="book"></app-project-book-cover-image>
  </div>

  <!-- ── TITLE BAR (compact) ── -->
  <div class="pt-title-bar">
    <!--
      Double click to rename in place, for the same reason the cover
      moved: a single click here is a selection now.

      A gesture nothing advertises is not a feature, so the overflow
      menu grew a Rename entry that does the same thing. That entry is
      the discoverable path; this one is the fast path for anyone who
      expects it to work.
    -->
    <div class="pt-title-content" *ngIf="!editingTitle" (dblclick)="startEditTitle($event)">
      <span class="pt-title">{{ book.title ? ((book.title.length > 50) ? (book.title | slice:0:50) + '…' : book.title) : ('No Title Set' | languageString) }}</span>
    </div>
    <input *ngIf="editingTitle" class="pt-title-input" #titleInput
           [value]="book.title || ''"
           maxlength="40"
           (blur)="commitTitle(titleInput.value)"
           (keydown.enter)="titleInput.blur()"
           (keydown.escape)="cancelTitle(titleInput)">

    <!-- Quick Actions -->
    <div class="pt-quick-actions">
      <button mat-icon-button class="pt-quick-btn" [matMenuTriggerFor]="bookMenu">
        <mat-icon>more_vert</mat-icon>
      </button>
    </div>
    <mat-menu #bookMenu="matMenu">
      <button mat-menu-item (click)="open(book)">
        <mat-icon>menu_book</mat-icon><span i18n="@@Open Book">Open Book</span>
      </button>
      <button mat-menu-item (click)="startRename()">
        <mat-icon>edit</mat-icon><span i18n="@@Rename">Rename</span>
      </button>
      <button mat-menu-item (click)="makePrimary(book, $event.target)" *ngIf="book !== book.project.primaryBook">
        <mat-icon>favorite</mat-icon><span i18n="@@Make Primary Book">Make Primary Book</span>
      </button>
      <button mat-menu-item (click)="makeDraft(book, $event.target)">
        <mat-icon>lock</mat-icon><span i18n="@@Make Draft Version of this Book">Make Draft Version of this Book</span>
      </button>
      <button mat-menu-item (click)="archive(book, $event.target)">
        <mat-icon>delete</mat-icon><span i18n="@@Delete Book">Delete Book</span>
      </button>
      <button mat-menu-item (click)="createNewProjectWithBook(book, $event.target)">
        <mat-icon>copy</mat-icon><span i18n="@@Create New Project With Book">Create New Project With Book</span>
      </button>
      <button mat-menu-item (click)="changeProject(book, $event.target)">
        <mat-icon>drive_file_move</mat-icon><span i18n="@@Move Book To A Different Project">Move Book To A Different Project</span>
      </button>
      <button mat-menu-item (click)="moveProject(book.project, $event.target)">
        <mat-icon>drive_file_move</mat-icon><span i18n="@@Move Project To A New Folder">Move Project To A New Folder</span>
      </button>
      <button mat-menu-item (click)="addToCart(book, $event.target)">
        <mat-icon>shopping_cart</mat-icon><span i18n="@@Add Book To Cart">Add Book To Cart</span>
      </button>
    </mat-menu>
  </div>

  <!-- ── BOOK DETAILS ── -->
  <div class="pt-sub-items">
    <div class="pt-sub-item pt-book">
      <div class="pt-sub-header">
        <mat-icon *ngIf="book === book.project.primaryBook" class="pt-primary-icon">favorite</mat-icon>
        <span class="pt-sub-label"><strong i18n="@@Book Prefix">Book:</strong> {{book.ID}}</span>
        <span class="pt-sub-age">({{getDaysOldString(book.lastUpdate)}})</span>
        <span class="pt-sub-actions">
          <button mat-icon-button class="pt-sub-action-btn" (click)="open(book)" i18n-matTooltip="@@Open Book" matTooltip="Open Book">
            <mat-icon>menu_book</mat-icon>
          </button>
        </span>
      </div>
      <div class="pt-sub-props">
        <span i18n-title="@@Trim Size" title="Trim Size">{{formatTrim(book.body?.width, book.body?.height)}}</span>
        <span i18n-title="@@Binding Type" title="Binding Type">{{getLanguageString(book.bindingType)}}</span>
        <span i18n-title="@@Interior Type" title="Interior Type">{{getLanguageString(book.interiorType)}}</span>
        <span i18n-title="@@Paper Type" title="Paper Type">{{getPaperTypeName(book.body?.paperType_ID)}}</span>
        <span i18n-title="@@Lamination Type" title="Lamination Type">{{getLanguageString(book.cover?.laminateType)}}</span>
      </div>
      <div class="pt-sub-resource">
        <span class="pt-res-label" i18n="@@Cover">Cover</span>
        <span class="pt-res-values">
          <span i18n-title="@@Cover Scale" title="Cover Scale">{{formatScale(book.cover?.documentScale)}}</span>
          <span i18n-title="@@Cover Vertical Shift" title="Cover Vertical Shift">{{formatShift(book.cover?.verticalShift)}}</span>
          <span i18n-title="@@Cover Horizontal Shift" title="Cover Horizontal Shift">{{formatShift(book.cover?.horizontalShift)}}</span>
        </span>
        <span class="pt-res-id"
              [style.background-color]="getResourceStatusColor(book.cover)"
              [style.background-image]="getResourceStripe(book.cover)"
              [class.res-badge-actionable]="isResourceActionable(book.cover)"
              [attr.role]="isResourceActionable(book.cover) ? 'button' : null"
              [attr.tabindex]="isResourceActionable(book.cover) ? 0 : null"
              (click)="onResourceBadgeClick(book.cover, true, $event)"
              (keydown.enter)="onResourceBadgeClick(book.cover, true, $event)"
              (keydown.space)="onResourceBadgeClick(book.cover, true, $event)"
              [title]="getResourceHoverText(book.cover, 'Cover Resource ID')">{{getResourceDisplayString(book.cover)}}</span>
        <app-preview-quality-indicator [compact]="true"
                                       [resourceId]="book.cover?.resource_ID"
                                       [standardized]="book.cover?.standardized"
                                       [standardizes]="true"></app-preview-quality-indicator>
      </div>
      <div class="pt-sub-resource">
        <span class="pt-res-label" i18n="@@Body">Body</span>
        <span class="pt-res-values">
          <span i18n-title="@@Body Scale" title="Body Scale">{{formatScale(book.body?.documentScale)}}</span>
          <span i18n-title="@@Body Left Vertical Shift" title="Body Left Vertical Shift">{{formatShift(book.body?.leftVerticalShift)}}</span>
          <span i18n-title="@@Body Left Horizontal Shift" title="Body Left Horizontal Shift">{{formatShift(book.body?.leftHorizontalShift)}}</span>
          <span i18n-title="@@Body Right Vertical Shift" title="Body Right Vertical Shift">{{formatShift(book.body?.rightVerticalShift)}}</span>
          <span i18n-title="@@Body Right Horizontal Shift" title="Body Right Horizontal Shift">{{formatShift(book.body?.rightHorizontalShift)}}</span>
        </span>
        <span class="pt-res-id"
              [style.background-color]="getResourceStatusColor(book.body)"
              [style.background-image]="getResourceStripe(book.body)"
              [class.res-badge-actionable]="isResourceActionable(book.body)"
              [attr.role]="isResourceActionable(book.body) ? 'button' : null"
              [attr.tabindex]="isResourceActionable(book.body) ? 0 : null"
              (click)="onResourceBadgeClick(book.body, false, $event)"
              (keydown.enter)="onResourceBadgeClick(book.body, false, $event)"
              (keydown.space)="onResourceBadgeClick(book.body, false, $event)"
              [title]="getResourceHoverText(book.body, 'Body Resource ID')">{{getResourceDisplayString(book.body)}}</span>
        <app-preview-quality-indicator [compact]="true"
                                       [resourceId]="book.body?.resource_ID"
                                       [standardized]="book.body?.standardized"
                                       [standardizes]="true"></app-preview-quality-indicator>
      </div>
    </div>
  </div>
</div>
